import { mapToCamelCase } from '@consigli/utils';
import { z } from 'zod';
import { getPaginationResponseSchema } from './pagination';
export var ReconciliationStatus;
(function (ReconciliationStatus) {
    ReconciliationStatus["ADDRESSED"] = "ADDRESSED";
    ReconciliationStatus["UNADDRESSED"] = "UNADDRESSED";
    ReconciliationStatus["INDETERMINATE"] = "INDETERMINATE";
    ReconciliationStatus["NA"] = "NA";
})(ReconciliationStatus || (ReconciliationStatus = {}));
export var ReconciliationConfidence;
(function (ReconciliationConfidence) {
    ReconciliationConfidence["HIGH"] = "HIGH";
    ReconciliationConfidence["MEDIUM"] = "MEDIUM";
    ReconciliationConfidence["LOW"] = "LOW";
})(ReconciliationConfidence || (ReconciliationConfidence = {}));
export var VersionCompareReviewStatus;
(function (VersionCompareReviewStatus) {
    VersionCompareReviewStatus["UNREVIEWED"] = "UNREVIEWED";
    VersionCompareReviewStatus["REVIEWED"] = "REVIEWED";
    VersionCompareReviewStatus["RESOLVED"] = "RESOLVED";
    VersionCompareReviewStatus["NOT_RELEVANT"] = "NOT_RELEVANT";
})(VersionCompareReviewStatus || (VersionCompareReviewStatus = {}));
export const versionComparisonSchema = z
    .object({
    id: z.number().int(),
    project_id: z.number().int(),
    document_package_id: z.string().uuid(),
    processor_name: z.string(),
    change_description: z.string(),
    blob_id: z.string().uuid().nullish(),
    blob_name: z.string().nullish(),
    content: z.string().nullish(),
    page_number: z.number().int().nullish(),
    x0: z.number().nullish(),
    y0: z.number().nullish(),
    x1: z.number().nullish(),
    y1: z.number().nullish(),
    // Only populated by the detail (retrieve) endpoint; the list omits it to avoid per-row SAS generation.
    file_url: z.string().nullish(),
    ref_blob_id: z.string().uuid().nullish(),
    ref_blob_name: z.string().nullish(),
    ref_content: z.string().nullish(),
    ref_page_number: z.number().int().nullish(),
    ref_x0: z.number().nullish(),
    ref_y0: z.number().nullish(),
    ref_x1: z.number().nullish(),
    ref_y1: z.number().nullish(),
    ref_file_url: z.string().nullish(),
    comment_blob_id: z.string().uuid().nullish(),
    comment_blob_name: z.string().nullish(),
    comment_text: z.string().nullish(),
    // Nothing is populated in comment_file_url from backend, just a placeholder.
    comment_file_url: z.string().nullish(),
    reconciliation_status: z.nativeEnum(ReconciliationStatus).nullish(),
    reconciliation_confidence: z.nativeEnum(ReconciliationConfidence).nullish(),
    // NOT NULL with a UNREVIEWED default on the backend, so every row carries it.
    review_status: z
        .nativeEnum(VersionCompareReviewStatus)
        .default(VersionCompareReviewStatus.UNREVIEWED),
    // Page-pairing confidence (0-100) for the A->B page match this record came from.
    match_confidence: z.number().int().nullish(),
    created_at: z.string(),
    updated_at: z.string(),
})
    .transform(mapToCamelCase);
export const getVersionComparisonsResponseSchema = getPaginationResponseSchema(versionComparisonSchema);
export const deleteAllVersionComparisonsResponseSchema = z.object({
    deleted_count: z.number().int(),
});
export const getVersionComparisonsUniqueBlobsResponseSchema = z.array(z
    .object({
    id: z.string(),
    name: z.string(),
})
    .transform(mapToCamelCase));
export const getVersionComparisonsReconciliationStatusCountResponseSchema = z.record(z.number().int());
export const versionComparisonSummarySchema = z
    .object({
    total_changes: z.number().int(),
    total_matched_changes: z.number().int(),
    total_comments: z.number().int(),
    total_addressed: z.number().int(),
    total_unaddressed: z.number().int(),
    total_indeterminate: z.number().int(),
    reconciliation_rate: z.number().int(),
    documents_analyzed: z.number().int(),
    documents_matched: z.number().int(),
    documents_unmatched: z.number().int(),
    // Defaulted so a summary response from an older backend (before these ship) still parses.
    pages_analyzed: z.number().int().default(0),
    pages_with_changes: z.number().int().default(0),
})
    .transform(mapToCamelCase);
